
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import {
  Box,
  TextField,
  Button,
  Autocomplete,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TablePagination,
} from '@mui/material';
import {
  FileDownload,
  PictureAsPdf,
  TableChart,
  Search,
} from '@mui/icons-material';
import { masterApi } from '../../../api/masterApi';
import { toolsApi } from '../../../api/toolsApi';
import { attendanceApi } from '../../../api/attendanceApi';
import { DropdownItem } from '../../../types';
import { toast } from 'react-toastify';

const attendanceStatusOptions = ['Present', 'Absent', 'HalfDay', 'Leave', 'CompOff'];

interface ReportRow {
  employeeName: string;
  employeeCode: string;
  department: string;
  presentDays: number;
  halfDayDays: number;
  absentDays: number;
  leaveDays: number;
  holidayDays: number;
  weekOffDays: number;
  lateDays: number;
  totalWorkingHours: string;
  avgDailyHours: string;
}

interface EmployeeFilterOption {
  id: string;
  name: string;
  employeeCode?: string;
}

const ROWS_PER_PAGE_OPTIONS = [10, 50, 100, { label: 'All', value: -1 }];

const getFirstDefined = (...values: any[]) => {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const toNumber = (value: any, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeReportRows = (rawData: any[]): ReportRow[] => {
  const rows = Array.isArray(rawData) ? rawData : [];

  const hasAggregatedShape = rows.some((item) =>
    getFirstDefined(item?.presentDays, item?.PresentDays, item?.absentDays, item?.AbsentDays) !== undefined
  );

  if (hasAggregatedShape) {
    return rows.map((item) => ({
      employeeName: getFirstDefined(item?.employeeName, item?.EmployeeName, 'Unknown'),
      employeeCode: getFirstDefined(item?.employeeCode, item?.EmployeeCode, '-'),
      department: getFirstDefined(item?.department, item?.Department, '-'),
      presentDays: toNumber(getFirstDefined(item?.presentDays, item?.PresentDays), 0),
      halfDayDays: toNumber(getFirstDefined(item?.halfDayDays, item?.HalfDayDays), 0),
      absentDays: toNumber(getFirstDefined(item?.absentDays, item?.AbsentDays), 0),
      leaveDays: toNumber(getFirstDefined(item?.leaveDays, item?.LeaveDays), 0),
      holidayDays: toNumber(getFirstDefined(item?.holidayDays, item?.HolidayDays), 0),
      weekOffDays: toNumber(getFirstDefined(item?.weekOffDays, item?.WeekOffDays), 0),
      lateDays: toNumber(getFirstDefined(item?.lateDays, item?.LateDays), 0),
      totalWorkingHours: String(getFirstDefined(item?.totalWorkingHours, item?.TotalWorkingHours, '0h 0m')),
      avgDailyHours: String(getFirstDefined(item?.avgDailyHours, item?.AvgDailyHours, '0h 0m')),
    }));
  }

  // Fallback for attendance-log style response from backend report handler.
  const grouped = new Map<string, ReportRow>();

  rows.forEach((item) => {
    const employeeName = String(getFirstDefined(item?.employeeName, item?.EmployeeName, 'Unknown'));
    const employeeCode = String(getFirstDefined(item?.employeeCode, item?.EmployeeCode, '-'));
    const department = String(getFirstDefined(item?.department, item?.Department, '-'));
    const key = `${employeeCode}__${employeeName}__${department}`;

    if (!grouped.has(key)) {
      grouped.set(key, {
        employeeName,
        employeeCode,
        department,
        presentDays: 0,
        halfDayDays: 0,
        absentDays: 0,
        leaveDays: 0,
        holidayDays: 0,
        weekOffDays: 0,
        lateDays: 0,
        totalWorkingHours: String(getFirstDefined(item?.totalWorkingHours, item?.TotalWorkingHours, '0h 0m')),
        avgDailyHours: String(getFirstDefined(item?.avgDailyHours, item?.AvgDailyHours, '0h 0m')),
      });
    }

    const current = grouped.get(key)!;
    const type = String(getFirstDefined(item?.type, item?.Type, '')).toLowerCase();
    const status = String(getFirstDefined(item?.status, item?.Status, '')).toLowerCase();

    if (status.includes('absent')) {
      current.absentDays += 1;
    } else if (status.includes('leave')) {
      current.leaveDays += 1;
    } else if (status.includes('late')) {
      current.lateDays += 1;
      current.presentDays += 1;
    } else if (status.includes('half')) {
      current.halfDayDays += 1;
    } else if (status.includes('present') || type === 'checkin') {
      current.presentDays += 1;
    }
  });

  return Array.from(grouped.values());
};

const AttendanceReportPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const tenantId = user?.tenantId;
  const today = new Date().toISOString().split('T')[0];

  const [dateRange, setDateRange] = useState({ from: today, to: today });
  const [department, setDepartment] = useState('');
  const [employee, setEmployee] = useState('');
  const [status, setStatus] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [reportData, setReportData] = useState<ReportRow[]>([]);
  const [departments, setDepartments] = useState<DropdownItem[]>([]);
  const [employees, setEmployees] = useState<EmployeeFilterOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generated, setGenerated] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filteredReportData = reportData.filter((row) =>
    row.employeeName.toLowerCase().includes(nameFilter.trim().toLowerCase())||
    row.employeeCode.toLowerCase().includes(nameFilter.trim().toLowerCase())
  );

  useEffect(() => {
    masterApi.getDepartments().then(setDepartments).catch(() => {});
    toolsApi.getEmployees().then((result: any[]) => {
      const normalized = (result || []).map((item) => ({
        id: String(getFirstDefined(item?.id, item?.Id, '')),
        name: String(getFirstDefined(item?.name, item?.Name, '')),
        employeeCode: getFirstDefined(item?.employeeCode, item?.EmployeeCode, undefined),
      })).filter((item) => item.id && item.name);
      setEmployees(normalized);
    }).catch(() => {});
  }, []);

  const buildFilters = useCallback(() => ({
    fromDate: dateRange.from || undefined,
    toDate: dateRange.to || undefined,
    department: department || undefined,
    employeeId: employee || undefined,
    status: status || undefined,
  }), [dateRange, department, employee, status]);

  const handleFromDateChange = (value: string) => {
    setError(null);
    setDateRange((prev) => ({
      from: value,
      to: prev.to && value && prev.to < value ? '' : prev.to,
    }));
  };

  const handleGenerateReport = async () => {
    
    if (dateRange.from && !dateRange.to) {
      setError('Please select To date.');
      return;
    }

    if (!dateRange.from && dateRange.to) {
      setError('Please select From date.');
      return;
    }

    if (dateRange.from && dateRange.to) {
      if (new Date(dateRange.from) > new Date(dateRange.to)) {
        setError('From date cannot be later than To date.');
        return;
      }
    }

    setError(null);

    if (dateRange.from && dateRange.from > today) {
      setError('From date cannot be in the future');
      return;
    }

    if (dateRange.to && dateRange.to > today) {
      setError('To date cannot be in the future');
      return;
    }

    if (dateRange.from && dateRange.to && dateRange.from > dateRange.to) {
      setError('From date cannot be later than To date');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await attendanceApi.getAttendanceReport(buildFilters());
      const apiResult = result as any;
      const rawData = getFirstDefined(
        apiResult?.data,
        apiResult?.Data,
        apiResult?.result?.data,
        apiResult?.result?.Data,
        []
      );

      const rows = normalizeReportRows(rawData);

      setReportData(rows);
      setGenerated(true);
      setPage(0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate report');
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'excel' | 'csv' | 'pdf') => {
    setExporting(true);
    try {
      const blob = await attendanceApi.exportReport({ ...buildFilters(), format });
      const ext = format === 'excel' ? 'xlsx' : format;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance-report.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || `Failed to export as ${format}`);
    } finally {
      setExporting(false);
    }
  };

  const paginatedReportData = rowsPerPage === -1
    ? filteredReportData
    : filteredReportData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Attendance Report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Generate attendance analytics and reports with export options.        
      </Typography>

      {/* Filters Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Filters
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              label="From"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: today }}
              value={dateRange.from}
              onChange={(e) => handleFromDateChange(e.target.value)}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={dateRange.to}
              inputProps={{ min: dateRange.from || undefined, max: today }}
              onChange={(e) => {
                setError(null);
                setDateRange({ ...dateRange, to: e.target.value });
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={department}
                label="Department"
                onChange={(e) => setDepartment(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.name}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              size="small"
              sx={{ minWidth: 260 }}
              options={employees}
              value={employees.find((emp) => emp.id === employee) || null}
              onChange={(_, newValue) => setEmployee(newValue?.id || '')}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) =>
                option.employeeCode ? `${option.name} (${option.employeeCode})` : option.name
              }
              renderInput={(params) => <TextField {...params} label="Employee" />}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {attendanceStatusOptions.map((item) => (
                  <MenuItem key={item} value={item}>{item}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Search />}
              onClick={handleGenerateReport}
              disabled={loading}
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Report results - only shown after generation */}
      {!loading && generated && (
        <>
      {/* Export Buttons */}
      {reportData.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<TableChart />}
              onClick={() => handleExport('excel')}
              disabled={exporting || reportData.length === 0}
            >
              Export Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={() => handleExport('csv')}
              disabled={exporting || reportData.length === 0}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<PictureAsPdf />}
              onClick={() => handleExport('pdf')}
              disabled={exporting || reportData.length === 0}
            >
              Export PDF
            </Button>
          </Box>
          <TextField
            size="small"
            label="Search Here..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 260 } }}
            placeholder='Name or Employee Code'
          />
        </Box>
      )}

      {/* Report Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Employee Name</strong></TableCell>
              <TableCell><strong>Employee Code</strong></TableCell>
              <TableCell><strong>Department</strong></TableCell>
              <TableCell align="center"><strong>Present</strong></TableCell>
              <TableCell align="center"><strong>Half Day</strong></TableCell>
              <TableCell align="center"><strong>Absent</strong></TableCell>
              <TableCell align="center"><strong>Leave</strong></TableCell>
              <TableCell align="center"><strong>Holiday</strong></TableCell>
              <TableCell align="center"><strong>WeekOff</strong></TableCell>
              <TableCell align="center"><strong>Late</strong></TableCell>
              <TableCell><strong>Total Hours</strong></TableCell>
              <TableCell><strong>Avg Daily Hours</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredReportData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {reportData.length === 0
                      ? 'No report data found for the selected filters.'
                      : 'No employees match the name filter.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
            paginatedReportData.map((row, idx) => (
              <TableRow key={idx} hover>
                <TableCell>{row.employeeName}</TableCell>
                <TableCell>{row.employeeCode}</TableCell>
                <TableCell>{row.department}</TableCell>
                <TableCell align="center">
                  <Chip label={row.presentDays} color="success" size="small" />
                </TableCell>
                <TableCell align="center">
                  <Chip label={row.halfDayDays} color="warning" size="small" />
                </TableCell>
                <TableCell align="center">
                  <Chip label={row.absentDays} color="error" size="small" />
                </TableCell>
                <TableCell align="center">
                  <Chip label={row.leaveDays} color="info" size="small" />
                </TableCell>
                <TableCell align="center">
                  <Chip label={row.holidayDays} color="secondary" size="small" />
                </TableCell>
                <TableCell align="center">
                  <Chip label={row.weekOffDays} color="default" size="small" />
                </TableCell>
                <TableCell align="center">
                  <Chip label={row.lateDays} color="warning" size="small" />
                </TableCell>
                <TableCell>{row.totalWorkingHours}</TableCell>
                <TableCell>{row.avgDailyHours}</TableCell>
              </TableRow>
            ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredReportData.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      />
        </>
      )}

      {/* Prompt when no report generated yet */}
      {!loading && !generated && !error && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="body1" color="text.secondary">
            Select your filters and click "Generate Report" to view attendance analytics.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AttendanceReportPage;
